import { useNavigate } from 'react-router-dom';

import { trackEvent } from '../analytics/posthog';
import { ArrowLeftIcon } from '../lib/icons';
import { Greeters } from './first-run';
import { LinkField } from './link-field';

/**
 * Where the panel waits on this screen, relative to the heading-to-field
 * column: two above the heading, leaning out past its ends, and two below the
 * field — so none of them sits on the copy or the field.
 */
const NEW_ROOM_PLACES: Record<string, string> = {
  Arjun: '-top-16 -left-24',
  Ava: 'top-full mt-8 -left-20',
  Hana: '-top-24 left-full -ml-6',
  Kofi: 'top-full mt-2 left-full -ml-12',
};

/**
 * "/new" — a new room, from home's "New Room" (or the empty state's "Start
 * first session"): one question, one field, and the same panel members
 * waiting around it as on the first visit. The canvas's dotted surface and a
 * way back to every room.
 */
export function NewRoomPage() {
  const navigate = useNavigate();

  return (
    <main className="relative isolate flex min-h-svh flex-col overflow-hidden bg-surface">
      {/* The board's own dotted surface. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.07] [background-image:radial-gradient(#1a1a1a_1px,transparent_1px)] [background-size:22px_22px]"
      />

      <button
        className="absolute top-6 left-6 flex h-11 cursor-pointer items-center gap-2 rounded-pill bg-surface pr-[18px] pl-3.5 text-ui font-bold text-text-primary shadow-bar transition hover:bg-surface-hover"
        onClick={() => {
          void navigate('/');
        }}
        type="button"
      >
        <ArrowLeftIcon aria-hidden="true" className="size-4" />
        All Rooms
      </button>

      <section className="mx-auto flex w-full max-w-[580px] flex-1 flex-col items-center justify-center px-6 pb-24 text-center">
        <div className="relative flex w-full flex-col items-center">
          <Greeters places={NEW_ROOM_PLACES} />

          {/* Title, underline and line under it, 4px apart, as designed;
              the title wider than the field so it holds two lines. */}
          <div className="relative flex flex-col items-center gap-[4px]">
            {/* The yellow dot from the design, off the title's left. */}
            <span
              aria-hidden="true"
              className="absolute top-0 -left-20 hidden size-6 rounded-pill bg-sunny xl:block"
            />
            <h1 className="!m-0 w-screen max-w-[720px] px-6 max-md:w-full max-md:px-0 !text-new-room-title !font-bold !text-text-primary duration-700 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none">
              Which page would you like feedback on?
            </h1>
            <img
              alt=""
              aria-hidden="true"
              className="pointer-events-none w-[260px] duration-700 animate-in fade-in motion-reduce:animate-none"
              src="/zero-state/squiggle-underline.svg"
            />
            <p className="max-w-[483px] text-body tracking-[-0.04em] text-text-secondary duration-700 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none">
              Paste a page link. Floto captures the full page and creates three
              AI personas based on its content, ready for a group chat with you.
            </p>
          </div>
          <div className="relative mt-[20px] w-full">
            <LinkField
              onStart={(url) => {
                trackEvent('session_started', { source: 'new_room' });
                void navigate(`/browse?url=${encodeURIComponent(url)}`);
              }}
              variant="hero"
            />
          </div>
        </div>
      </section>
    </main>
  );
}
